const PDP_SELECTOR = '[data-testid="product-information"]';
const PERSONALISER_SELECTOR = '#teeinblue-form';
const TEE_PRICE_SELECTOR = '#teeinblue-form .tee-product-price';
const THEME_PRICE_SELECTOR = '.product-details product-price [ref="priceContainer"]';
const stickyPersonalisationControllers = new WeakMap();

function normalisePrice(value = '') {
  return value.replace(/\s+/g, ' ').trim();
}

function updateDuplicatePrice(root = document) {
  const appPrice = root.querySelector(TEE_PRICE_SELECTOR) || document.querySelector(TEE_PRICE_SELECTOR);
  const themePriceContainer = root.querySelector(THEME_PRICE_SELECTOR) || document.querySelector(THEME_PRICE_SELECTOR);
  if (!appPrice || !themePriceContainer) return;

  const appValue = normalisePrice(appPrice.textContent);
  const themeValues = Array.from(themePriceContainer.querySelectorAll('.price, .compare-at-price'))
    .map((price) => normalisePrice(price.textContent))
    .filter(Boolean);
  const isDuplicate = appValue !== '' && themeValues.includes(appValue);

  appPrice.hidden = isDuplicate;
  appPrice.toggleAttribute('data-ll-duplicate-price', isDuplicate);
}

function getFieldName(control) {
  const field = control.closest('.tee-field, .tee-form-field, .tee-block, [class*="field"]');
  const heading = field?.querySelector(
    '.tee-field__heading, .tee-field__title, .tee-option__title, .tee-radio-label, legend, label:not(:has(input))'
  );
  return heading?.textContent?.replace('*', '').replace(/\s+/g, ' ').trim() || '';
}

function enhancePersonaliserAccessibility(root = document) {
  const personaliser = root.matches?.(PERSONALISER_SELECTOR) ? root : root.querySelector?.(PERSONALISER_SELECTOR);
  if (!personaliser) return;

  personaliser.setAttribute('aria-label', 'Personalise this product');
  personaliser.setAttribute('tabindex', '-1');
  personaliser.style.scrollMarginTop = 'calc(var(--header-height, 64px) + 16px)';

  personaliser.querySelectorAll('input, textarea, select, button').forEach((control) => {
    if (!(control instanceof HTMLElement) || control.getAttribute('type') === 'hidden') return;
    const hasName = control.getAttribute('aria-label') || control.getAttribute('aria-labelledby');
    const hasLabel = control.id && document.querySelector(`label[for="${CSS.escape(control.id)}"]`);
    if (!hasName && !hasLabel) {
      const fieldName = getFieldName(control);
      if (fieldName) control.setAttribute('aria-label', fieldName);
    }
  });

  personaliser.querySelectorAll('.tee-error').forEach((error) => {
    error.setAttribute('role', 'alert');
    error.setAttribute('aria-live', 'assertive');
    error.setAttribute('aria-atomic', 'true');
  });
}

function initialiseStickyPersonalisationLink(root = document) {
  const jump = root.querySelector?.('[data-ll-pdp-jump]') || document.querySelector('[data-ll-pdp-jump]');
  if (!jump) return;

  const existingController = stickyPersonalisationControllers.get(jump);
  if (existingController) {
    existingController.refresh();
    return;
  }

  const abortController = new AbortController();
  let animationFrame = 0;
  let showFrame = 0;
  let hideTimer = 0;
  let visibleState = false;

  const isRendered = (element) => {
    if (!element) return false;
    const styles = window.getComputedStyle(element);
    const bounds = element.getBoundingClientRect();
    return styles.display !== 'none' && styles.visibility !== 'hidden' && bounds.width > 0 && bounds.height > 0;
  };

  const intersectsViewport = (element, topInset = 0, bottomInset = 0) => {
    if (!isRendered(element)) return false;
    const bounds = element.getBoundingClientRect();
    return bounds.bottom > topInset && bounds.top < window.innerHeight - bottomInset;
  };

  const setVisible = (visible, immediate = false) => {
    const shouldShow = Boolean(visible);

    if (shouldShow === visibleState) {
      if (!shouldShow && immediate && !jump.hidden) {
        window.clearTimeout(hideTimer);
        hideTimer = 0;
        jump.hidden = true;
      }
      return;
    }

    visibleState = shouldShow;

    if (shouldShow) {
      window.clearTimeout(hideTimer);
      hideTimer = 0;
      jump.hidden = false;
      jump.setAttribute('aria-hidden', 'false');
      jump.tabIndex = 0;

      window.cancelAnimationFrame(showFrame);
      showFrame = window.requestAnimationFrame(() => {
        showFrame = 0;
        if (visibleState && jump.isConnected) jump.dataset.llVisible = 'true';
      });
      return;
    }

    window.cancelAnimationFrame(showFrame);
    showFrame = 0;
    jump.dataset.llVisible = 'false';
    jump.setAttribute('aria-hidden', 'true');
    jump.tabIndex = -1;
    if (document.activeElement === jump) jump.blur();

    window.clearTimeout(hideTimer);
    hideTimer = 0;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hideDelay = immediate || reduceMotion ? 0 : 380;

    if (hideDelay === 0) {
      jump.hidden = true;
      return;
    }

    hideTimer = window.setTimeout(() => {
      hideTimer = 0;
      if (!visibleState) jump.hidden = true;
    }, hideDelay);
  };

  const update = () => {
    animationFrame = 0;
    if (!jump.isConnected) {
      window.cancelAnimationFrame(showFrame);
      window.clearTimeout(hideTimer);
      abortController.abort();
      return;
    }

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const productSection = document.querySelector(PDP_SELECTOR);
    const personaliser = document.querySelector(PERSONALISER_SELECTOR);
    const intro = productSection?.querySelector('.ll-pdp-cro');
    const footer = document.querySelector('footer');
    const footerBoundary = footer?.closest('.shopify-section-group-footer-group') || footer;
    const header = document.querySelector('#header-component, .header-section');
    const headerBottom = header?.getBoundingClientRect().bottom || 0;
    const topInset = Math.max(8, Math.min(headerBottom, window.innerHeight * 0.2));
    const bottomInset = 72;
    const introBounds = isRendered(intro) ? intro.getBoundingClientRect() : null;
    const footerBounds = isRendered(footerBoundary) ? footerBoundary.getBoundingClientRect() : null;
    const introPassed = !introBounds || introBounds.bottom <= topInset + 8;
    const introVisible = intersectsViewport(intro, topInset, bottomInset);
    const personaliserVisible = intersectsViewport(personaliser, topInset, bottomInset);
    const footerReached = Boolean(footerBounds && footerBounds.top < window.innerHeight);

    const shouldShow =
      isMobile &&
        window.scrollY >= 120 &&
        Boolean(personaliser) &&
        introPassed &&
        !introVisible &&
        !personaliserVisible &&
        !footerReached;

    setVisible(shouldShow, !isMobile || footerReached);
  };

  const scheduleUpdate = () => {
    if (animationFrame) return;
    animationFrame = window.requestAnimationFrame(update);
  };

  jump.dataset.llReady = 'true';
  jump.dataset.llVisible = 'false';
  jump.setAttribute('aria-hidden', 'true');
  jump.tabIndex = -1;
  jump.hidden = true;

  window.addEventListener('scroll', scheduleUpdate, { passive: true, signal: abortController.signal });
  window.addEventListener('resize', scheduleUpdate, { passive: true, signal: abortController.signal });
  window.addEventListener('orientationchange', scheduleUpdate, { passive: true, signal: abortController.signal });

  jump.addEventListener(
    'click',
    (event) => {
      const personaliser = document.querySelector(PERSONALISER_SELECTOR);
      if (!personaliser) return;

      event.preventDefault();
      setVisible(false);
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      personaliser.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      window.setTimeout(() => personaliser.focus({ preventScroll: true }), reduceMotion ? 0 : 450);
    },
    { signal: abortController.signal }
  );

  stickyPersonalisationControllers.set(jump, { refresh: scheduleUpdate });
  scheduleUpdate();
}

function initialiseProductCRO(root = document) {
  enhancePersonaliserAccessibility(root);
  updateDuplicatePrice(root);
  initialiseStickyPersonalisationLink(root);

  const productSection = document.querySelector(PDP_SELECTOR);
  if (!productSection || productSection.dataset.llCroObserver === 'true') return;
  productSection.dataset.llCroObserver = 'true';

  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      enhancePersonaliserAccessibility(productSection);
      updateDuplicatePrice(productSection);
      initialiseStickyPersonalisationLink(productSection);
    });
  });

  observer.observe(productSection, { childList: true, subtree: true, characterData: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initialiseProductCRO());
} else {
  initialiseProductCRO();
}

document.addEventListener('shopify:section:load', (event) => initialiseProductCRO(event.target));
